import {
  CAROUSEL_TILE_COUNTS,
  CAROUSEL_TILE_RATIOS,
  GRID_ROW_COUNTS,
  SplitKind,
  type CarouselTileCount,
  type GridRowCount,
  type SplitSettings,
} from '@/core/document'
import { EditAction } from '@/core/history'
import { useEditSession } from '@/core/store/editSession'
import { HelpTopic } from '@/content/help'
import { TEST_IDS } from '@/lib/testIds'
import { ChoiceGroup, ChoiceTile } from '@/ui/ChoiceTile'
import { InfoTip } from '@/ui/InfoTip'
import { ShapeIllustration, SplitIllustration } from '@/ui/illustrations/PanelIllustrations'
import { Stepper } from '@/ui/Stepper'

const DEFAULT_CAROUSEL: SplitSettings = {
  kind: SplitKind.Carousel,
  tiles: 3,
  tileRatio: { ...CAROUSEL_TILE_RATIOS[0] },
}
const DEFAULT_GRID: SplitSettings = { kind: SplitKind.Grid, rows: 1 }
const MIN_CAROUSEL_TILES = CAROUSEL_TILE_COUNTS[0] ?? (() => { throw new Error('Carousel tile counts require a minimum') })()
const MAX_CAROUSEL_TILES = CAROUSEL_TILE_COUNTS.at(-1) ?? (() => { throw new Error('Carousel tile counts require a maximum') })()
const MIN_GRID_ROWS = GRID_ROW_COUNTS[0] ?? (() => { throw new Error('Grid row counts require a minimum') })()
const MAX_GRID_ROWS = GRID_ROW_COUNTS.at(-1) ?? (() => { throw new Error('Grid row counts require a maximum') })()

function ratioKey(ratio: { w: number; h: number }): string {
  return `${ratio.w}:${ratio.h}`
}

function ratioLabel(ratio: { w: number; h: number }): string {
  return ratio.w === ratio.h ? 'Square' : 'Portrait'
}

export function SplitPicker() {
  const document = useEditSession((state) => state.document)
  const setSplit = useEditSession((state) => state.setSplit)
  const commitHistory = useEditSession((state) => state.commitHistory)
  if (!document) throw new Error('Split picker requires a loaded document')
  const split = document.output.split

  const updateSplit = (next: SplitSettings | null) => {
    setSplit(next)
    commitHistory(EditAction.ChangeOutputSplit)
  }

  return (
    <div className="space-y-3">
      <ChoiceGroup mode="single" label="Split output" className="grid-cols-3" data-testid={TEST_IDS.splitMode}>
        <ChoiceTile
          ariaLabel="Single"
          label="Single"
          illustration={<SplitIllustration kind="single" />}
          selected={split === null}
          onClick={() => updateSplit(null)}
        />
        <ChoiceTile
          ariaLabel="Carousel"
          label="Carousel"
          illustration={<SplitIllustration kind={SplitKind.Carousel} />}
          selected={split?.kind === SplitKind.Carousel}
          onClick={() => updateSplit(DEFAULT_CAROUSEL)}
        />
        <ChoiceTile
          ariaLabel="Grid"
          label="Grid"
          illustration={<SplitIllustration kind={SplitKind.Grid} />}
          selected={split?.kind === SplitKind.Grid}
          onClick={() => updateSplit(DEFAULT_GRID)}
        />
      </ChoiceGroup>

      {split?.kind === SplitKind.Carousel ? (
        <div className="flex items-center gap-3 rounded-xl bg-muted/60 p-3">
          <Stepper
            label="Carousel slides"
            value={split.tiles}
            min={MIN_CAROUSEL_TILES}
            max={MAX_CAROUSEL_TILES}
            data-testid={TEST_IDS.splitTiles}
            onChange={(tiles) => {
              if (!CAROUSEL_TILE_COUNTS.includes(tiles as CarouselTileCount)) throw new Error(`Unsupported carousel tile count: ${tiles}`)
              updateSplit({ ...split, tiles: tiles as CarouselTileCount })
            }}
          />
          <ChoiceGroup mode="single" label="Carousel slide shape" className="flex-1 grid-cols-2" data-testid={TEST_IDS.splitTileRatio}>
            {CAROUSEL_TILE_RATIOS.map((ratio) => (
              <ChoiceTile
                key={ratioKey(ratio)}
                ariaLabel={ratioLabel(ratio)}
                label={ratioLabel(ratio)}
                illustration={<ShapeIllustration ratio={ratio} />}
                selected={ratioKey(split.tileRatio) === ratioKey(ratio)}
                onClick={() => updateSplit({ ...split, tileRatio: { ...ratio } })}
              />
            ))}
          </ChoiceGroup>
        </div>
      ) : null}

      {split?.kind === SplitKind.Grid ? (
        <div className="flex items-center justify-between rounded-xl bg-muted/60 p-3">
          <Stepper
            label="Grid rows"
            value={split.rows}
            min={MIN_GRID_ROWS}
            max={MAX_GRID_ROWS}
            data-testid={TEST_IDS.splitRows}
            onChange={(rows) => {
              if (!GRID_ROW_COUNTS.includes(rows as GridRowCount)) throw new Error(`Unsupported grid row count: ${rows}`)
              updateSplit({ ...split, rows: rows as GridRowCount })
            }}
          />
          <InfoTip topic={HelpTopic.SplitReassembly} />
        </div>
      ) : null}
    </div>
  )
}
